import React, { useState, useEffect } from 'react';
import AlertModal from './AlertModal';

const API = 'https://n293vk0zm6.execute-api.us-east-1.amazonaws.com/prod';
const PAGE_SIZE = 20;

const OUTCOME_CONFIG = {
  went_viral:   { color: 'var(--accent)', label: 'Went viral' },
  partial_rise: { color: 'var(--amber)', label: 'Partial rise' },
  no_change:    { color: 'var(--neutral)', label: 'No change' },
  pending:      { color: 'var(--neutral)', label: 'Pending' },
};

function modelsLabel(models_fired) {
  if (models_fired === 'isolation_forest+kmeans') return { text: 'Both',             teal: true  };
  if (models_fired === 'isolation_forest')        return { text: 'Isolation Forest', teal: false };
  if (models_fired === 'kmeans')                  return { text: 'K-Means',          teal: false };
  return { text: '—', teal: false };
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr + 'T00:00:00Z').toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', timeZone: 'UTC'
  });
}

function sortRows(rows) {
  const closed  = rows
    .filter(r => r.outcome && r.outcome !== 'pending')
    .sort((a, b) => a.detected_date.localeCompare(b.detected_date));
  const pending = rows
    .filter(r => !r.outcome || r.outcome === 'pending')
    .sort((a, b) => a.detected_date.localeCompare(b.detected_date));
  return [...closed, ...pending];
}

function OutcomeCell({ outcome, spots_climbed, days_left }) {
  if (outcome === 'pending') {
    return (
      <span style={{
        fontSize: '10px', background: 'var(--bg)', color: 'var(--muted)',
        padding: '2px 8px', borderRadius: '20px', border: '1px solid var(--border)'
      }}>
        Pending · {days_left} day{days_left !== 1 ? 's' : ''} left
      </span>
    );
  }
  const cfg   = OUTCOME_CONFIG[outcome] || OUTCOME_CONFIG.no_change;
  const arrow = spots_climbed > 0 ? '↑' : spots_climbed < 0 ? '↓' : '→';
  const abs   = Math.abs(spots_climbed);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
      <span style={{ fontSize: '12px', fontWeight: outcome === 'went_viral' ? '600' : 500, color: cfg.color }}>
        {arrow} {abs} spot{abs !== 1 ? 's' : ''}
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
        <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: cfg.color, flexShrink: 0 }} />
        <span style={{ fontSize: '11px', fontWeight: outcome === 'went_viral' ? '600' : 400, color: cfg.color }}>{cfg.label}</span>
      </div>
    </div>
  );
}

export default function PredictionHistory({ allAlerts = [] }) {
  const [rows, setRows]               = useState([]);
  const [loading, setLoading]         = useState(true);
  const [page, setPage]               = useState(1);
  const [selectedAlert, setSelectedAlert] = useState(null);

  useEffect(() => {
    fetch(`${API}/alerts?all=true`)
      .then(r => r.json())
      .then(data => {
        setRows(sortRows(data.alerts || []));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const closed  = rows.filter(r => r.outcome && r.outcome !== 'pending');
  const pending = rows.filter(r => !r.outcome || r.outcome === 'pending');

  const totalPages  = Math.ceil(rows.length / PAGE_SIZE);
  const pageRows    = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const precisionFor = (model_type) => {
    const subset = model_type === 'all'
      ? closed
      : closed.filter(r => r.models_fired === model_type);
    if (!subset.length) return { pct: null, n: 0 };
    const hits = subset.filter(r =>
      r.outcome === 'went_viral' || r.outcome === 'partial_rise'
    ).length;
    return { pct: Math.round(hits / subset.length * 100), n: subset.length };
  };

  const flagHistory = {};
  allAlerts.forEach(a => {
    const tid = a.track_id?.toLowerCase();
    if (!tid) return;
    if (!flagHistory[tid]) flagHistory[tid] = [];
    flagHistory[tid].push(a.detected_date);
  });
  Object.values(flagHistory).forEach(dates => dates.sort());

  const bothPrec = precisionFor('isolation_forest+kmeans');
  const ifPrec   = precisionFor('isolation_forest');
  const kmPrec   = precisionFor('kmeans');

  const thStyle = {
    fontSize: '10px', color: 'var(--muted)', textAlign: 'left',
    padding: '10px 14px', textTransform: 'uppercase', letterSpacing: '0.4px',
    background: 'var(--bg)', borderBottom: '1px solid var(--border)',
    whiteSpace: 'nowrap'
  };
  const tdStyle = {
    fontSize: '12px', color: 'var(--sub)',
    padding: '11px 14px', borderBottom: '1px solid var(--border)'
  };

  const btnStyle = (disabled) => ({
    fontSize: '12px', padding: '6px 14px',
    border: '1px solid var(--border)', borderRadius: '8px',
    background: disabled ? 'var(--bg)' : 'var(--surface)',
    color: disabled ? 'var(--muted)' : 'var(--text)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1
  });

  return (
    <div>
      {/* Explainer */}
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: '10px', padding: '16px', marginBottom: '16px'
      }}>
        <div style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text)', marginBottom: '10px' }}>
          How predictions are evaluated
        </div>
        <div className="prediction-explainer-grid">
          <div style={{ background: 'var(--bg)', borderRadius: '8px', padding: '10px 12px' }}>
            <div style={{ fontSize: '10px', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '4px' }}>What is a prediction</div>
            <div style={{ fontSize: '12px', color: 'var(--text)', lineHeight: 1.6 }}>
              Every track flagged by the ML model is treated as a prediction that it will show chart momentum within 7 days — an early signal that often precedes viral growth.
            </div>
          </div>
          <div style={{ background: 'var(--bg)', borderRadius: '8px', padding: '10px 12px' }}>
            <div style={{ fontSize: '10px', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '4px' }}>How outcome is measured</div>
            <div style={{ fontSize: '12px', color: 'var(--text)', lineHeight: 1.6 }}>
              The best rank achieved within 7 days of being flagged is compared to the rank at the time of the alert.
            </div>
          </div>
          <div style={{ background: 'var(--bg)', borderRadius: '8px', padding: '10px 12px' }}>
            <div style={{ fontSize: '10px', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '6px' }}>Outcome thresholds</div>
            {[
              { color: 'var(--accent)', label: 'Went viral',   desc: 'climbed 20% or more of remaining rank positions' },
              { color: 'var(--amber)', label: 'Partial rise', desc: 'climbed 5% to 19% of remaining rank positions' },
              { color: 'var(--neutral)', label: 'No change',    desc: 'climbed less than 5% or dropped' },
            ].map(t => (
              <div key={t.label} style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginBottom: '6px' }}>
                <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: t.color, flexShrink: 0, marginTop: '4px' }} />
                <span style={{ fontSize: '12px', color: 'var(--text)', lineHeight: 1.5 }}>
                  <strong>{t.label}</strong> — {t.desc}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{
          textAlign: 'center', padding: '40px 20px', color: 'var(--muted)',
          fontSize: '13px', background: 'var(--surface)',
          borderRadius: '10px', border: '1px solid var(--border)', marginBottom: '16px'
        }}>
          Loading prediction history...
        </div>
      ) : rows.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '40px 20px', color: 'var(--muted)',
          fontSize: '13px', background: 'var(--surface)',
          borderRadius: '10px', border: '1px solid var(--border)', marginBottom: '16px'
        }}>
          No alerts yet.
        </div>
      ) : (
        <div style={{
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: '10px', overflow: 'hidden', marginBottom: '16px'
        }}>
          <div className="prediction-table-wrap" style={{
            overflowX: 'auto', WebkitOverflowScrolling: 'touch'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '640px' }}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, width: '160px' }}>Track</th>
                  <th style={{ ...thStyle, width: '120px' }}>Artist</th>
                  <th style={{ ...thStyle, width: '100px' }}>Date flagged</th>
                  <th style={{ ...thStyle, width: '70px'  }}>Score</th>
                  <th style={{ ...thStyle, width: '120px' }}>Models fired</th>
                  <th style={{ ...thStyle, width: '90px'  }}>Rank flagged</th>
                  <th style={{ ...thStyle, width: '80px'  }}>Best rank</th>
                  <th style={{ ...thStyle, width: '150px' }}>Outcome</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row, i) => {
                  const ml         = modelsLabel(row.models_fired);
                  const isLast     = i === pageRows.length - 1;
                  const rowTdStyle = { ...tdStyle, borderBottom: isLast ? 'none' : '1px solid var(--border)' };
                  const score      = parseFloat(row.anomaly_score);
                  const isPending  = !row.outcome || row.outcome === 'pending';

                  const tid         = row.track_id?.toLowerCase();
                  const history     = flagHistory[tid] || [];
                  const signalIndex = history.indexOf(row.detected_date);
                  const signalNum   = signalIndex + 1;

                  return (
                    <tr
                      key={`${row.track_id}-${row.detected_date}`}
                      onClick={() => setSelectedAlert(row)}
                      onMouseEnter={e => e.currentTarget.querySelectorAll('td').forEach(td => td.style.background = 'var(--row)')}
                      onMouseLeave={e => e.currentTarget.querySelectorAll('td').forEach(td => td.style.background = '')}
                      style={{ opacity: isPending ? 0.75 : 1, cursor: 'pointer' }}
                    >
                      <td style={rowTdStyle}>
                        <span style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text)' }}>{row.name}</span>
                        {signalNum > 1 && (
                          <div style={{
                            fontSize: '9px', padding: '1px 6px', borderRadius: '20px',
                            background: 'var(--purple-bg)', color: 'var(--purple)',
                            border: '1px solid var(--purple-bd)', display: 'inline-block', marginTop: '2px'
                          }}>
                            {signalNum === 2 ? '2nd signal' : signalNum === 3 ? '3rd signal' : `${signalNum}th signal`}
                          </div>
                        )}
                      </td>
                      <td style={rowTdStyle}>{row.artist}</td>
                      <td style={rowTdStyle}>{formatDate(row.detected_date)}</td>
                      <td style={{ ...rowTdStyle, color: score >= 0.6 ? 'var(--accent)' : 'var(--amber)', fontWeight: 500 }}>
                        {Math.round(score * 100)}%
                      </td>
                      <td style={rowTdStyle}>
                        <span style={{
                          fontSize: '10px', padding: '2px 8px', borderRadius: '20px',
                          background: ml.teal ? 'var(--accent-bg)' : 'transparent',
                          color: ml.teal ? 'var(--accent)' : 'var(--muted)',
                          border: ml.teal ? '1px solid var(--accent-bd)' : 'none'
                        }}>
                          {ml.text}
                        </span>
                      </td>
                      <td style={rowTdStyle}>#{row.rank}</td>
                      <td style={rowTdStyle}>
                        {row.best_rank ? `#${row.best_rank}` : '—'}
                      </td>
                      <td style={rowTdStyle}>
                        <OutcomeCell
                          outcome={row.outcome}
                          spots_climbed={row.spots_climbed}
                          days_left={row.days_left}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 14px', borderTop: '1px solid var(--border)',
              background: 'var(--bg)'
            }}>
              <span style={{ fontSize: '11px', color: 'var(--muted)' }}>
                Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, rows.length)} of {rows.length}
              </span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  style={btnStyle(page === 1)}
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  ← Prev
                </button>
                <span style={{ fontSize: '11px', color: 'var(--muted)', alignSelf: 'center' }}>
                  Page {page} of {totalPages}
                </span>
                <button
                  style={btnStyle(page === totalPages)}
                  disabled={page === totalPages}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Precision section */}
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: '10px', padding: '16px'
      }}>
        <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text)', marginBottom: '4px' }}>
          Signal precision comparison
        </div>
        <div style={{ fontSize: '12px', color: 'var(--sub)', lineHeight: 1.6, marginBottom: '16px' }}>
          {closed.length > 0
            ? `Based on ${closed.length} alert${closed.length !== 1 ? 's' : ''} with closed outcome windows.`
            : 'Precision figures will update as 7-day outcome windows close.'
          }
        </div>

        <div className="prediction-precision-grid">
          {[
            {
              title:  'Isolation Forest + K-Means',
              prec:   bothPrec,
              target: 71,
              color:  'var(--accent)',
              desc:   'Both models independently flagged the same track. Highest confidence signal.'
            },
            {
              title:  'Isolation Forest only',
              prec:   ifPrec,
              target: 58,
              color:  'var(--neutral)',
              desc:   "Isolation Forest detected a spike vs the track's own baseline."
            },
            {
              title:  'K-Means only',
              prec:   kmPrec,
              target: 45,
              color:  'var(--neutral)',
              desc:   'K-Means detected a cluster shift but Isolation Forest saw no spike.'
            },
          ].map(p => {
            const displayPct = p.prec.n > 0 ? p.prec.pct : p.target;
            const isReal     = p.prec.n > 0;
            return (
              <div key={p.title} style={{ background: 'var(--bg)', borderRadius: '8px', padding: '12px' }}>
                <div style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text)', marginBottom: '2px' }}>{p.title}</div>
                {isReal && (
                  <div style={{ fontSize: '10px', color: 'var(--accent)', marginBottom: '6px' }}>
                    Real data · {p.prec.n} alert{p.prec.n !== 1 ? 's' : ''}
                  </div>
                )}
                {!isReal && (
                  <div style={{ fontSize: '10px', color: 'var(--muted)', marginBottom: '6px' }}>
                    Projected target
                  </div>
                )}
                <div style={{ height: '22px', background: 'var(--border)', borderRadius: '4px', overflow: 'hidden', marginBottom: '6px' }}>
                  <div style={{ width: `${displayPct}%`, height: '100%', background: isReal ? 'var(--accent)' : p.color, borderRadius: '4px', display: 'flex', alignItems: 'center', paddingLeft: '8px' }}>
                    <span style={{ fontSize: '11px', fontWeight: 500, color: '#fff' }}>{displayPct}%</span>
                  </div>
                </div>
                <div style={{ fontSize: '11px', color: 'var(--muted)', lineHeight: 1.5 }}>{p.desc}</div>
              </div>
            );
          })}
        </div>

        <div style={{ height: '1px', background: 'var(--border)', margin: '14px 0' }} />

        <div style={{ fontSize: '12px', color: 'var(--sub)', lineHeight: 1.7 }}>
          <span style={{ color: 'var(--text)', fontWeight: 500 }}>{rows.length}</span> total alert{rows.length !== 1 ? 's' : ''} ·{' '}
          <span style={{ color: 'var(--text)', fontWeight: 500 }}>{closed.length}</span> closed ·{' '}
          <span style={{ color: 'var(--text)', fontWeight: 500 }}>{pending.length}</span> pending
        </div>
        {closed.length === 0 && (
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '8px', fontStyle: 'italic' }}>
            Projected targets shown — real precision updates as outcome windows close.
          </div>
        )}
      </div>

      <AlertModal
        alert={selectedAlert}
        alerts={rows}
        allAlerts={allAlerts}
        onClose={() => setSelectedAlert(null)}
      />
    </div>
  );
}